// Signalements automatiques des prises (spec anti-perte phase 1, §5.2.6) : pour
// « Incomplète » et « Problème signalé », par le flux de « Signaler un problème »
// (Edge Function `signalements` → base Notion Signalements), sans geste de la
// personne. Un seul signalement par prise et par état, même après plusieurs
// passages de l'exécuteur ou plusieurs lancements — sauf un envoi reçu par le
// serveur dont la réponse se perd : non marqué, il est refait (plutôt deux
// fiches qu'aucune).
//
// Contenu (spec §7, même périmètre qu'ADR 0025) : diagnostics natifs (au premier
// niveau, comme « Signaler un problème »), manifeste résumé et identifiant de
// capture. Ni audio, ni titre, ni nom de personne, ni
// transcription : les métadonnées du manifeste sont réduites au type et au
// projet, et aucun geste n'est joint. Le serveur envoie son propre signalement
// d'assemblage (S4) ; la description cite l'id de capture pour rapprocher les
// deux fiches.
//
// `Diagnostics` (qui appelle `registerPlugin` à l'import) et le client Supabase
// sont chargés PARESSEUSEMENT, comme dans `SignalerProbleme.tsx`.
import { Capacitor } from "@capacitor/core";
import type { DeviceInfo } from "../native/diagnostics";
import { type Contexte, lireContexte } from "../signalement/contexte";
import type { EnvoyerSignalementInput } from "../signalement/envoyer";
import { conservedMs } from "./take-lifecycle";
import { parseTakeMeta } from "./take-meta";
import type { TakeManifest } from "./take-types";
import { formatTrashDuration } from "./trash";

/** Clé du magasin de dédoublonnage : tableau JSON de `"<takeId>:<kind>"`. */
export const AUTO_SIGNALEMENTS_KEY = "captureBesoins.autoSignalements";

/**
 * Délai au-delà duquel un signalement est abandonné — construction (magasin,
 * client, appareil, diagnostics natifs) ET envoi : une source ou un réseau qui
 * ne répond pas ne bloque jamais la file. Non marqué, réessayé au passage suivant.
 */
export const AUTO_SIGNALEMENT_TIMEOUT_MS = 10_000;

const TIMED_OUT = Symbol("délai dépassé");

// Aucune de ces sources n'accepte de signal d'annulation : on borne l'attente.
// Une fois abandonné, le signalement ne part jamais en retard ; seul un envoi
// DÉJÀ lancé peut encore aboutir après coup. Il est alors marqué, et tant qu'il
// est en vol, un appel pour la même prise et le même état n'en lance pas un
// second (`inFlight`).
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | typeof TIMED_OUT> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), ms);
  });
  return Promise.race([p, timeout]).finally(() => clearTimeout(timer));
}

export type AutoSignalementKind = "incomplete" | "problem";

export interface AutoSignaleurDeps {
  kv: { get(k: string): Promise<string | null>; set(k: string, v: string): Promise<void> };
  envoyer: (input: EnvoyerSignalementInput) => Promise<{ ok: boolean }>;
  /** Diagnostics de l'enregistreur natif (`CapacitorAudioRecorder.getDiagnostics`). */
  diagnostics: () => Promise<unknown>;
  /** Injectables (tests) ; par défaut, les mêmes sources que « Signaler un problème ». */
  appareil?: () => Promise<DeviceInfo>;
  contexte?: () => Contexte;
  client?: () => Promise<EnvoyerSignalementInput["client"]>;
}

const LIBELLES: Record<AutoSignalementKind, string> = {
  incomplete: "incomplet",
  problem: "impossible à préparer",
};

function defaultContexte(): Contexte {
  return lireContexte({
    location: window.location,
    navigator: window.navigator,
    screen: window.screen,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    now: new Date(),
    version: __APP_VERSION__,
    natif: Capacitor.isNativePlatform(),
  });
}

async function defaultAppareil(): Promise<DeviceInfo> {
  const { Diagnostics } = await import("../native/diagnostics");
  return Diagnostics.deviceInfo();
}

async function defaultClient(): Promise<EnvoyerSignalementInput["client"]> {
  const { getSupabaseClient } = await import("../supabase/client");
  return getSupabaseClient();
}

// Segments illisibles cités par leur nom : au plus ce nombre (le compte, lui, est exact).
const MAX_UNREADABLE_NAMES = 20;

/**
 * Manifeste joint au signalement, RÉSUMÉ (revue finale N-I1) : jamais segment
 * par segment — 180 segments pesaient ~15 000 caractères et Notion coupait la
 * suite (`lastError` compris). Métadonnées métier réduites au type et au projet.
 */
export function manifestForReport(m: TakeManifest) {
  const meta = parseTakeMeta(m.meta);
  const unreadable = m.segments.filter((seg) => typeof seg.durationMs !== "number");
  return {
    takeId: m.takeId,
    captureId: m.captureId ?? null,
    state: m.state,
    mergeAttempts: m.mergeAttempts,
    startedAt: m.startedAt,
    stoppedAt: m.stoppedAt ?? null,
    elapsedMs: m.elapsedMs ?? null,
    meta: meta ? { type: meta.type, projectId: meta.projectId } : null,
    segments: {
      count: m.segments.length,
      unreadableCount: unreadable.length,
      unreadable: unreadable.slice(0, MAX_UNREADABLE_NAMES).map((seg) => seg.name),
      bytes: m.segments.reduce((t, seg) => t + (seg.bytes ?? 0), 0),
      first: m.segments[0]?.name ?? null,
      last: m.segments.at(-1)?.name ?? null,
    },
    uploadedSegments: { count: m.uploadedSegments.length },
    merged: m.merged ?? null,
    lastError: m.lastError ?? null,
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

// Groupes « en vrac » résumés comme les prises du natif : nombre, premier,
// dernier — un nom par fichier (180 le 24-09) ferait couper le reste.
function legacyGroupsForReport(groups: unknown): unknown {
  if (!Array.isArray(groups)) return groups;
  return groups.map((g) => {
    if (!isRecord(g) || !Array.isArray(g.files)) return g;
    const { files, ...rest } = g;
    return {
      groupId: rest.groupId,
      count: files.length,
      first: files[0] ?? null,
      last: files.at(-1) ?? null,
      totalMs: rest.totalMs,
      startedAt: rest.startedAt,
    };
  });
}

/**
 * Diagnostics du signalement (revue finale N-I1) : ceux du natif AU PREMIER
 * NIVEAU, comme « Signaler un problème » — l'Edge Function en extrait le
 * journal (`log`) et le résumé « Enregistrement » (`status`, `durationMs`,
 * `files`, `trash`) —, puis l'état, la capture et le manifeste résumé à côté.
 */
function diagnosticsForReport(kind: AutoSignalementKind, m: TakeManifest, natif: unknown) {
  const base = isRecord(natif)
    ? {
        ...natif,
        ...("legacyGroups" in natif
          ? { legacyGroups: legacyGroupsForReport(natif.legacyGroups) }
          : {}),
      }
    : {};
  return { ...base, etat: kind, captureId: m.captureId ?? null, manifeste: manifestForReport(m) };
}

function description(kind: AutoSignalementKind, m: TakeManifest): string {
  const duree = formatTrashDuration(conservedMs(m));
  return (
    `Signalement automatique : enregistrement ${LIBELLES[kind]} (${duree}) — ` +
    `capture ${m.captureId ?? "sans identifiant"}, prise ${m.takeId}`
  );
}

async function readSent(kv: AutoSignaleurDeps["kv"]): Promise<string[]> {
  const raw = await kv.get(AUTO_SIGNALEMENTS_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === "string") : [];
  } catch {
    return [];
  }
}

export function createAutoSignaleur(
  o: AutoSignaleurDeps,
): (kind: AutoSignalementKind, m: TakeManifest) => Promise<void> {
  const appareil = o.appareil ?? defaultAppareil;
  const contexte = o.contexte ?? defaultContexte;
  const client = o.client ?? defaultClient;

  async function nativeDiagnostics(): Promise<unknown> {
    try {
      return await o.diagnostics();
    } catch {
      return null;
    }
  }

  async function markSent(key: string): Promise<void> {
    const sent = await readSent(o.kv);
    if (!sent.includes(key)) await o.kv.set(AUTO_SIGNALEMENTS_KEY, JSON.stringify([...sent, key]));
  }

  // Envois lancés et pas encore aboutis (réponse, puis marque), par clé. Un
  // envoi dont l'attente a été abandonnée (délai dépassé) y reste jusqu'à sa
  // réponse : le passage suivant relirait le magasin avant la marque tardive
  // et enverrait une seconde fiche.
  const inFlight = new Set<string>();

  async function signalOnce(
    kind: AutoSignalementKind,
    m: TakeManifest,
    abandoned: () => boolean,
  ): Promise<void> {
    const key = `${m.takeId}:${kind}`;
    // Envoi déjà en vol pour cette clé : il marquera la prise s'il aboutit ;
    // sinon le passage suivant réessaiera.
    if (inFlight.has(key)) return;
    if ((await readSent(o.kv)).includes(key)) return;
    const input: EnvoyerSignalementInput = {
      client: await client(),
      description: description(kind, m),
      gravite: "contournable",
      contexte: contexte(),
      appareil: await appareil(),
      // Aucun geste joint : ils peuvent citer un nom, et personne n'a rien fait.
      fil: [],
      diagnostics: diagnosticsForReport(kind, m, await nativeDiagnostics()),
      erreur: null,
      capture: null,
    };
    // Délai dépassé pendant la construction : la file est déjà repartie, ne
    // rien envoyer en retard (le prochain passage refera tout), ni doubler un
    // envoi en vol.
    if (abandoned() || inFlight.has(key)) return;
    inFlight.add(key);
    try {
      const r = await o.envoyer(input);
      // Envoi refusé ou hors ligne : pas marqué, le prochain passage réessaie.
      if (r.ok) await markSent(key);
    } finally {
      inFlight.delete(key);
    }
  }

  async function boundedSignal(kind: AutoSignalementKind, m: TakeManifest): Promise<void> {
    let abandoned = false;
    const r = await withTimeout(
      signalOnce(kind, m, () => abandoned),
      AUTO_SIGNALEMENT_TIMEOUT_MS,
    );
    if (r === TIMED_OUT) {
      abandoned = true;
      console.warn(
        `[auto-signalement] ${m.takeId}:${kind} : pas de réponse en ${AUTO_SIGNALEMENT_TIMEOUT_MS} ms`,
      );
    }
  }

  // File série : deux appels simultanés pour la même prise ne lisent pas le
  // magasin avant que le premier l'ait écrit (un seul envoi). Si le premier a
  // dépassé le délai, c'est `inFlight` qui empêche le second envoi.
  let queue: Promise<void> = Promise.resolve();
  return (kind, m) => {
    const run = queue.then(async () => {
      try {
        await boundedSignal(kind, m);
      } catch (err) {
        // Ne lève jamais : un signalement raté ne doit rien bloquer ; le
        // prochain passage réessaie (rien n'a été marqué).
        console.warn("[auto-signalement] envoi impossible :", err);
      }
    });
    queue = run;
    return run;
  };
}
